import { Box, IconButton, useBreakpointValue } from "@chakra-ui/react"
import useKeyControl from "@pathwright/utils/src/keyboard/useKeyControl"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import Tooltip from "@pathwright/ux/src/components/tooltip/Tooltip"
import { useMemo } from "react"
import { useLibraryResources } from "../library/browse/useLibraryResources"
import { getStoreResourceUrl } from "../utils/urls"
import { useCoverPageContext } from "./CoverPageProvider"

const CoverPageLibraryNavigation = () => {
  const { resource, loadingNextCoverPage, setLoadingNextCoverPage } =
    useCoverPageContext()
  const libraryResources = useLibraryResources()
  const courses = libraryResources?.courses || ([] as any[])
  const isMobile = useBreakpointValue({
    base: true,
    md: false
  })

  // Find current course index in the courses array
  const currentIndex = useMemo(() => {
    if (!resource?.id || !courses?.length) return -1
    return courses.findIndex((course) => course.id === resource.id)
  }, [resource?.id, courses])

  // Get next and previous courses
  const { prevCourse, nextCourse } = useMemo(() => {
    if (currentIndex === -1 || !courses?.length) {
      return { prevCourse: null, nextCourse: null }
    }

    const prevIndex = currentIndex > 0 ? currentIndex - 1 : courses.length - 1
    const nextIndex = currentIndex < courses.length - 1 ? currentIndex + 1 : 0

    return {
      prevCourse: courses[prevIndex],
      nextCourse: courses[nextIndex]
    }
  }, [currentIndex, courses])

  const handlePrevClick = () => {
    if (prevCourse && !loadingNextCoverPage) {
      setLoadingNextCoverPage?.(true)
      window.App?.navigate(getStoreResourceUrl(prevCourse.slug, true))
    }
  }

  const handleNextClick = () => {
    if (nextCourse && !loadingNextCoverPage) {
      setLoadingNextCoverPage?.(true)
      window.App?.navigate(getStoreResourceUrl(nextCourse.slug, true))
    }
  }

  useKeyControl("ArrowLeft", handlePrevClick)
  useKeyControl("ArrowRight", handleNextClick)

  // Don't render on mobile
  if (isMobile) {
    return null
  }

  // Don't render if we don't have navigation options
  if (!prevCourse && !nextCourse) {
    return null
  }

  const ArrowButton = ({ direction }: { direction: "prev" | "next" }) => {
    return (
      <Box
        position="absolute"
        top="50%"
        transform="translateY(-50%)"
        left={direction === "prev" ? "-50px" : undefined}
        right={direction === "next" ? "-50px" : undefined}
        zIndex={12}
      >
        <Tooltip
          title={
            (direction === "prev" ? prevCourse.name : nextCourse.name) || ""
          }
        >
          <IconButton
            aria-label={`${direction === "prev" ? "Previous" : "Next"} course`}
            icon={
              <Pathicon
                icon={`chevron-${direction === "prev" ? "left" : "right"}`}
                color="blackAlpha.600"
                size="1.4rem"
              />
            }
            onClick={direction === "prev" ? handlePrevClick : handleNextClick}
            size="lg"
            variant="ghost"
            isRound
            height="44px"
            width="44px"
            minWidth="44px"
            minHeight="44px"
            bgColor="whiteAlpha.600"
            backdropFilter="blur(20px)"
            borderRadius="full"
            isLoading={loadingNextCoverPage}
            isDisabled={loadingNextCoverPage}
            _hover={{
              bgColor: "whiteAlpha.800"
            }}
            _active={{
              bgColor: "whiteAlpha.900"
            }}
          />
        </Tooltip>
      </Box>
    )
  }

  return (
    <>
      {/* Previous Arrow */}
      {prevCourse && <ArrowButton direction="prev" />}
      {nextCourse && <ArrowButton direction="next" />}
    </>
  )
}

export default CoverPageLibraryNavigation
